import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Link,
  Button,
  Tooltip,
  Collapse,
} from "@mui/material";
import {
  Copy,
  FilePlus2,
  ChevronDown,
  ChevronRight,
  Mail,
  MessageSquare,
} from "lucide-react";
import StreamingTextBox from "components/common/StreamingTextBox";
import WithDividers from "components/helpers/WithDividers";
import {
  BookmarkItem,
  AlertItem,
  ChatMessage,
  FunctionCall,
} from "components/hooks/useTaskAgentState";
import SimpleRichEditor, { Handle } from "components/editor/SimpleRichEditor";
import { RichContent } from "components/editor/utils";
import useCached from "components/hooks/useCached";
import { JSONContent } from "@tiptap/react";
import AgentAlert from "./AgentAlert";
import FlexBox from "components/common/FlexBox";
import DocumentViewer from "components/editor/DocumentViewer";
import { ChatMessageTable, PartialChatMessageTable, cleanHref } from "./Tables";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ToolOutputsSection } from "./ToolOutputsSection";

// XML Component mapping
const XML_COMPONENT_MAP = {
  table: "ChatMessageTable",
  b: "bold",
  i: "italic",
  h1: "heading1",
  h2: "heading2",
  h3: "heading3",
  h4: "heading4",
  h5: "heading5",
  h6: "heading6",
  quote: "quote",
  a: "link",
  ul: "unorderedList",
  ol: "orderedList",
  br: "lineBreak",
  code: "code",
  // Add more XML components here as needed
};

// XML Component renderers (also used by createNodeRenderer)
export const XmlBold = ({
  children,
  block,
}: {
  children: React.ReactNode;
  block?: boolean;
}) => (
  <Typography
    component="span"
    sx={{
      fontWeight: "bold",
      display: block ? "block" : "inline",
      lineHeight: "inherit",
    }}
  >
    {children}
  </Typography>
);

export const XmlItalic = ({
  children,
  block,
}: {
  children: React.ReactNode;
  block?: boolean;
}) => (
  <Typography
    component="span"
    sx={{
      fontStyle: "italic",
      display: block ? "block" : "inline",
      lineHeight: "inherit",
    }}
  >
    {children}
  </Typography>
);

// Shared recursive node renderer for nested XML tags
export const createNodeRenderer = () => {
  const renderNode = (node: ChildNode): React.ReactNode => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const children = Array.from(element.childNodes).map((child, idx) => (
        <React.Fragment key={idx}>{renderNode(child)}</React.Fragment>
      ));

      switch (element.tagName.toLowerCase()) {
        case "b":
          return <XmlBold>{children}</XmlBold>;
        case "i":
          return <XmlItalic>{children}</XmlItalic>;
        case "h1":
        case "a":
          const href = element.getAttribute("href");
          return href ? (
            <Link
              href={cleanHref(href)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {children}
            </Link>
          ) : (
            <>{children}</>
          );
        default:
          return <>{children}</>;
      }
    }

    return null;
  };

  return renderNode;
};

const XmlHeading1 = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="h4" sx={{ marginY: 2, fontWeight: "bold" }}>
    {children}
  </Typography>
);

const XmlHeading2 = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="h5" sx={{ marginY: 1.5, fontWeight: "bold" }}>
    {children}
  </Typography>
);

const XmlHeading3 = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="h6" sx={{ marginY: 1, fontWeight: "bold" }}>
    {children}
  </Typography>
);

const XmlHeading4 = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="h4" sx={{ marginY: 1, fontWeight: "bold" }}>
    {children}
  </Typography>
);

const XmlHeading5 = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="h5" sx={{ marginY: 1, fontWeight: "bold" }}>
    {children}
  </Typography>
);

const XmlHeading6 = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="h6" sx={{ marginY: 1, fontWeight: "bold" }}>
    {children}
  </Typography>
);

const XmlQuote = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      borderLeft: "4px solid #e0e0e0",
      paddingLeft: 2,
      marginY: 1,
      fontStyle: "italic",
      color: "text.secondary",
    }}
  >
    {children}
  </Box>
);

const XmlCode = ({
  xmlContent,
  pushSnack,
}: {
  xmlContent: string;
  pushSnack?: (snack: {
    message: string;
    alertSeverity: "success" | "error" | "warning" | "info";
    key: string;
  }) => void;
}) => {
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlContent, "text/html");
  const codeElement = doc.querySelector("code");
  if (!codeElement) return <>{xmlContent}</>;

  const content = codeElement.textContent || "";
  const type = codeElement.getAttribute("type");
  const emails = codeElement.getAttribute("emails");
  const emailSubject = codeElement.getAttribute("emailSubject");
  const number = codeElement.getAttribute("number");

  const isEmail = type === "email";
  const isText = type === "text";

  const handleClick = () => {
    if (isEmail) {
      const params = new URLSearchParams();
      if (emails) {
        params.set("email", emails);
      }
      if (emailSubject) {
        params.set("emailSubject", emailSubject);
      }
      if (content) {
        params.set("emailBody", content);
      }
      navigate(`/inbox/email/create?${params.toString()}`);
    }
    if (isText) {
      if (number) {
        const params = new URLSearchParams();
        params.set("phone", number);
        if (content) {
          params.set("textMessage", content);
        }
        navigate(`/inbox/phone/create?${params.toString()}`);
      } else {
        setSearchParams((p) => {
          p.set("textMessage", content);
          return p;
        });
      }
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    pushSnack?.({
      message: "Content copied to clipboard",
      alertSeverity: "success",
      key: "content-copied",
    });
  };

  const getTooltip = () => {
    if (isEmail) {
      return emails ? `Email to: ${emails}` : "Email draft";
    }
    if (isText) {
      return number ? `Message to: ${number}` : "Text message draft";
    }
    return "Draft";
  };

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: "#fafafa",
        border: "1px solid #e0e0e0",
        borderLeft: `2px solid #198282`,
        borderRadius: "4px",
        padding: "12px 16px",
        marginY: 1,
        fontFamily: "inherit",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "8px",
          right: "12px",
          display: "flex",
          gap: "8px",
        }}
      >
        <Tooltip title="Copy to clipboard">
          <IconButton size="small" onClick={handleCopy}>
            <Copy size={20} />
          </IconButton>
        </Tooltip>
        {(isEmail || isText) && (
          <Tooltip title={getTooltip()}>
            <IconButton size="small" onClick={handleClick}>
              {isEmail ? <Mail size={20} /> : <MessageSquare size={20} />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {isEmail && emailSubject && (
        <Typography
          variant="bodySmall"
          sx={{
            fontWeight: 600,
            marginBottom: "8px",
            color: "text.secondary",
          }}
        >
          Subject: {emailSubject}
        </Typography>
      )}
      <Typography
        variant="body"
        sx={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {content}
      </Typography>
    </Box>
  );
};

const XmlLink = ({
  xmlContent,
  block,
}: {
  xmlContent: string;
  block?: boolean;
}) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlContent, "text/html");
  const link = doc.querySelector("a");
  if (!link) return <>{xmlContent}</>;

  const href = link.getAttribute("href");
  const content = link.textContent;

  if (!href) return <>{content}</>;

  return (
    <Link
      href={cleanHref(href)}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        ...(block && {
          display: "block",
        }),
      }}
    >
      {content}
    </Link>
  );
};

// Shared function for rendering list items with nested content
const createListItemRenderer = (
  renderNode: (node: ChildNode) => React.ReactNode,
) => {
  const renderListItem = (li: Element, index: number) => {
    const children = Array.from(li.childNodes).map((child, idx) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const element = child as Element;
        if (element.tagName.toLowerCase() === "ul") {
          // Handle nested ul elements
          return (
            <Box
              key={idx}
              component="ul"
              sx={{
                marginY: 0.5,
                paddingLeft: 3,
                listStyleType: "disc",
                marginLeft: 2,
              }}
            >
              {Array.from(element.querySelectorAll("li")).map(
                (nestedLi, nestedIndex) =>
                  renderListItem(nestedLi, nestedIndex),
              )}
            </Box>
          );
        }
      }
      return <React.Fragment key={idx}>{renderNode(child)}</React.Fragment>;
    });

    return (
      <Typography
        key={index}
        component="li"
        variant="body"
        sx={{ marginBottom: 0.5, display: "list-item" }}
      >
        {children}
      </Typography>
    );
  };

  return renderListItem;
};

const XmlUnorderedList = ({ xmlContent }: { xmlContent: string }) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlContent, "text/html");
  const ul = doc.querySelector("ul");

  if (!ul) return null;

  const renderNode = createNodeRenderer();
  const renderListItem = createListItemRenderer(renderNode);

  // Get only direct li children of the ul (not nested ones)
  const directItems = Array.from(ul.children).filter(
    (child) => child.tagName.toLowerCase() === "li",
  );

  return (
    <Box
      component="ul"
      sx={{ marginY: 1, paddingLeft: 3, listStyleType: "disc" }}
    >
      {directItems.map((li, index) => renderListItem(li, index))}
    </Box>
  );
};

const PartialXmlUnorderedList = ({ xmlContent }: { xmlContent: string }) => {
  // Parse incomplete list - extract complete <li> items
  const completeItemMatches = xmlContent.match(/<li>.*?<\/li>/gs) || [];

  if (completeItemMatches.length === 0) return null;

  const renderNode = createNodeRenderer();
  const renderListItem = createListItemRenderer(renderNode);
  const parser = new DOMParser();

  const items = completeItemMatches
    .map((itemXml) => {
      const doc = parser.parseFromString(itemXml, "text/html");
      return doc.querySelector("li");
    })
    .filter((li) => li !== null);

  return (
    <Box
      component="ul"
      sx={{ marginY: 1, paddingLeft: 3, listStyleType: "disc" }}
    >
      {items.map((li, index) => (li ? renderListItem(li, index) : null))}
    </Box>
  );
};

const XmlOrderedList = ({ xmlContent }: { xmlContent: string }) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlContent, "text/html");
  const ol = doc.querySelector("ol");

  if (!ol) return null;

  const renderNode = createNodeRenderer();
  const renderListItem = createListItemRenderer(renderNode);

  const directItems = Array.from(ol.children).filter(
    (child) => child.tagName.toLowerCase() === "li",
  );

  return (
    <Box
      component="ol"
      sx={{ marginY: 1, paddingLeft: 3, listStyleType: "decimal" }}
    >
      {directItems.map((li, index) => renderListItem(li, index))}
    </Box>
  );
};

const PartialXmlOrderedList = ({ xmlContent }: { xmlContent: string }) => {
  const completeItemMatches = xmlContent.match(/<li>.*?<\/li>/gs) || [];

  if (completeItemMatches.length === 0) return null;

  const renderNode = createNodeRenderer();
  const renderListItem = createListItemRenderer(renderNode);
  const parser = new DOMParser();

  const items = completeItemMatches
    .map((itemXml) => {
      const doc = parser.parseFromString(itemXml, "text/html");
      return doc.querySelector("li");
    })
    .filter((li) => li !== null);

  return (
    <Box
      component="ol"
      sx={{ marginY: 1, paddingLeft: 3, listStyleType: "decimal" }}
    >
      {items.map((li, index) => (li ? renderListItem(li, index) : null))}
    </Box>
  );
};

// Individual entry components
const BookmarkEntry = ({ bookmark }: { bookmark: BookmarkItem }) => {
  const typeEmoji = {
    task: "📋",
    person: "👤",
    document: "📄",
    location: "📍",
    resource: "🔗",
    idea: "💡",
    issue: "⚠️",
  };

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="flex-start"
      gap="12px"
      sx={{
        padding: "8px 0",
      }}
    >
      <Box sx={{ minWidth: "20px", fontSize: "16px" }}>
        {typeEmoji[bookmark.type] || "🔖"}
      </Box>
      <Box flex="1">
        <Box
          display="flex"
          alignItems="center"
          gap="8px"
          sx={{ marginBottom: "4px" }}
        >
          <Typography
            variant="body"
            sx={{
              fontWeight: 600,
              color: "#3D3D3D",
            }}
          >
            {bookmark.title}
          </Typography>
          <Typography
            variant="bodySmall"
            sx={{
              backgroundColor: "#f0f0f0",
              color: "#666",
              padding: "2px 6px",
              borderRadius: "4px",
              fontSize: "0.75rem",
              textTransform: "uppercase",
              fontWeight: 500,
            }}
          >
            {bookmark.type}
          </Typography>
        </Box>
        <Typography
          variant="bodySmall"
          color="text.secondary"
          sx={{ marginBottom: "2px" }}
        >
          {bookmark.reason}
        </Typography>
        <Typography
          variant="bodySmall"
          color="text.secondary"
          sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
        >
          ID: {bookmark.id}
        </Typography>
      </Box>
    </Box>
  );
};

// Section components
export const BookmarkSection = ({
  bookmarks,
}: {
  bookmarks: BookmarkItem[];
}) => {
  if (bookmarks.length === 0) return null;

  return (
    <Box>
      <Typography variant="bodyHeavy" sx={{ marginBottom: "8px" }}>
        Bookmarks
        <Typography
          component="span"
          variant="bodySmall"
          sx={{
            marginLeft: "8px",
            backgroundColor: "#e3f2fd",
            color: "#1565c0",
            padding: "2px 6px",
            borderRadius: "4px",
            fontSize: "0.75rem",
          }}
        >
          {bookmarks.length} items
        </Typography>
      </Typography>
      <WithDividers>
        {bookmarks.map((bookmark) => (
          <BookmarkEntry key={bookmark.id} bookmark={bookmark} />
        ))}
      </WithDividers>
    </Box>
  );
};

export const ChatMessageSection = ({
  messages,
  functionCalls,
  isCurrentlyActive,
  onCreateDocument,
}: {
  messages: ChatMessage[];
  functionCalls: FunctionCall[];
  isCurrentlyActive: boolean;
  onCreateDocument?: (content: string) => void;
}) => {
  if (!messages || messages.length === 0) return null;
  const { pushSnack } = useSnackpack();
  // Parse messages to extract XML blocks and render special components
  const parseMessages = (text: string) => {
    const components: Array<{
      type: "text" | "xml";
      content: string;
      componentType?: string;
      newlinePrefixed?: boolean;
      newlineSuffixed?: boolean;
    }> = [];
    let currentPos = 0;

    // Find XML blocks - improved regex to handle nested structures
    const xmlRegex = /<(\w+)([^>]*?)>/g;
    let match;

    while ((match = xmlRegex.exec(text)) !== null) {
      const tagName = match[1];
      const startPos = match.index;

      // Check if this is a known XML component
      if (XML_COMPONENT_MAP[tagName as keyof typeof XML_COMPONENT_MAP]) {
        // Add text before XML block (trim newlines but preserve spaces)
        if (startPos > currentPos) {
          const textContent = text
            .substring(currentPos, startPos)
            .replace(/^[\n\r]+|[\n\r]+$/g, "");
          if (textContent) {
            components.push({
              type: "text",
              content: textContent,
              newlinePrefixed: currentPos > 0 && text[currentPos - 1] === "\n",
              newlineSuffixed: text[startPos - 1] === "\n",
            });
          }
        }

        // For ul/ol tags, we need to find the matching closing tag
        // accounting for nested list structures
        if (tagName === "ul" || tagName === "ol") {
          const closingTag = `</${tagName}>`;
          let endPos = text.indexOf(closingTag, startPos);
          let depth = 1;
          let searchPos = startPos + match[0].length;

          // Handle nested list tags by counting opening and closing tags
          while (endPos !== -1 && depth > 0) {
            const nextOpen = text.indexOf(`<${tagName}`, searchPos);
            const nextClose = text.indexOf(`</${tagName}>`, searchPos);

            if (nextOpen !== -1 && nextOpen < nextClose) {
              depth++;
              searchPos = nextOpen + tagName.length + 1;
            } else if (nextClose !== -1) {
              depth--;
              if (depth > 0) {
                searchPos = nextClose + tagName.length + 3;
                endPos = text.indexOf(closingTag, searchPos);
              }
            } else {
              break;
            }
          }

          if (endPos !== -1) {
            const xmlBlock = text.substring(
              startPos,
              endPos + closingTag.length,
            );
            const xmlEndPos = endPos + closingTag.length;
            components.push({
              type: "xml",
              content: xmlBlock,
              componentType: tagName,
              newlinePrefixed: startPos > 0 && text[startPos - 1] === "\n",
              newlineSuffixed:
                xmlEndPos < text.length && text[xmlEndPos] === "\n",
            });

            currentPos = endPos + closingTag.length;
            xmlRegex.lastIndex = currentPos;
          } else if (isCurrentlyActive) {
            const partialXmlBlock = text.substring(startPos);
            components.push({
              type: "xml",
              content: partialXmlBlock,
              componentType:
                tagName === "ul" ? "PartialList" : "PartialOrderedList",
              newlinePrefixed: startPos > 0 && text[startPos - 1] === "\n",
              newlineSuffixed: false,
            });
            currentPos = text.length;
            break;
          } else {
            const textContent = text
              .substring(currentPos, startPos + match[0].length)
              .replace(/^[\n\r]+|[\n\r]+$/g, "");
            if (textContent) {
              components.push({
                type: "text",
                content: textContent,
                newlinePrefixed:
                  currentPos > 0 && text[currentPos - 1] === "\n",
                newlineSuffixed: text[startPos + match[0].length - 1] === "\n",
              });
            }
            currentPos = startPos + match[0].length;
          }
        } else if (tagName === "br") {
          // Handle self-closing br tag
          components.push({
            type: "xml",
            content: match[0],
            componentType: "br",
            newlinePrefixed: startPos > 0 && text[startPos - 1] === "\n",
            newlineSuffixed:
              startPos + match[0].length < text.length &&
              text[startPos + match[0].length] === "\n",
          });
          currentPos = startPos + match[0].length;
          xmlRegex.lastIndex = currentPos;
        } else {
          // Handle other XML components (table, b, i, etc.)
          const closingTag = `</${tagName}>`;
          const endPos = text.indexOf(closingTag, startPos);

          if (endPos !== -1) {
            // Extract the complete XML block
            const xmlBlock = text.substring(
              startPos,
              endPos + closingTag.length,
            );
            const xmlEndPos = endPos + closingTag.length;
            components.push({
              type: "xml",
              content: xmlBlock,
              componentType: tagName,
              newlinePrefixed: startPos > 0 && text[startPos - 1] === "\n",
              newlineSuffixed:
                xmlEndPos < text.length && text[xmlEndPos] === "\n",
            });

            currentPos = endPos + closingTag.length;
            xmlRegex.lastIndex = currentPos;
          } else if (tagName === "table" && isCurrentlyActive) {
            // For progressive loading, parse incomplete table
            const partialXmlBlock = text.substring(startPos);
            components.push({
              type: "xml",
              content: partialXmlBlock,
              componentType: "PartialTable",
              newlinePrefixed: startPos > 0 && text[startPos - 1] === "\n",
              newlineSuffixed: false,
            });
            currentPos = text.length;
            break;
          } else {
            // If no closing tag found, treat as regular text
            const textContent = text
              .substring(currentPos, startPos + match[0].length)
              .replace(/^[\n\r]+|[\n\r]+$/g, "");
            if (textContent) {
              components.push({
                type: "text",
                content: textContent,
                newlinePrefixed:
                  currentPos > 0 && text[currentPos - 1] === "\n",
                newlineSuffixed: text[startPos + match[0].length - 1] === "\n",
              });
            }
            currentPos = startPos + match[0].length;
          }
        }
      }
    }

    // Add remaining text
    if (currentPos < text.length) {
      const textContent = text
        .substring(currentPos)
        .replace(/^[\n\r]+|[\n\r]+$/g, "");
      if (textContent) {
        components.push({
          type: "text",
          content: textContent,
          newlinePrefixed: currentPos > 0 && text[currentPos - 1] === "\n",
          newlineSuffixed: text[text.length - 1] === "\n",
        });
      }
    }

    // Strip out any text components that are just whitespace.
    const finalComponents = components.filter((component) => {
      return component.content.trim() !== "";
    });
    return finalComponents;
  };

  const renderComponent = (
    component: {
      type: "text" | "xml";
      content: string;
      componentType?: string;
      newlinePrefixed?: boolean;
      newlineSuffixed?: boolean;
    },
    index: number | string,
    showCursor: boolean = false,
  ) => {
    if (component.type === "xml" && component.componentType) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(component.content, "text/html");
      const bodyElement = doc.documentElement;

      const getTextContent = (tagName: string) => {
        // If the root element is the tag we're looking for, use it directly
        if (bodyElement?.tagName?.toLowerCase() === tagName.toLowerCase()) {
          return bodyElement.textContent || "";
        }
        // Otherwise, look for the tag within the root element
        const element = bodyElement?.querySelector(tagName);
        return element?.textContent || "";
      };

      switch (component.componentType) {
        case "table":
        case "Table":
          return (
            <ChatMessageTable key={index} xmlContent={component.content} />
          );
        case "PartialTable":
          return (
            <PartialChatMessageTable
              key={index}
              xmlContent={component.content}
            />
          );
        case "b":
          return (
            <XmlBold
              block={component.newlinePrefixed && component.newlineSuffixed}
              key={index}
            >
              {getTextContent("b")}
            </XmlBold>
          );
        case "i":
          return (
            <XmlItalic
              block={component.newlinePrefixed && component.newlineSuffixed}
              key={index}
            >
              {getTextContent("i")}
            </XmlItalic>
          );
        case "br":
          return <br key={index} />;
        case "h1":
          return <XmlHeading1 key={index}>{getTextContent("h1")}</XmlHeading1>;
        case "h2":
          return <XmlHeading2 key={index}>{getTextContent("h2")}</XmlHeading2>;
        case "h3":
          return <XmlHeading3 key={index}>{getTextContent("h3")}</XmlHeading3>;
        case "h4":
          return <XmlHeading4 key={index}>{getTextContent("h4")}</XmlHeading4>;
        case "h5":
          return <XmlHeading5 key={index}>{getTextContent("h5")}</XmlHeading5>;
        case "h6":
          return <XmlHeading6 key={index}>{getTextContent("h6")}</XmlHeading6>;
        case "quote":
          return <XmlQuote key={index}>{getTextContent("quote")}</XmlQuote>;
        case "a":
          return (
            <XmlLink
              block={component.newlinePrefixed && component.newlineSuffixed}
              key={index}
              xmlContent={component.content}
            />
          );
        case "ul":
          return (
            <XmlUnorderedList key={index} xmlContent={component.content} />
          );
        case "PartialList":
          return (
            <PartialXmlUnorderedList
              key={index}
              xmlContent={component.content}
            />
          );
        case "ol":
          return <XmlOrderedList key={index} xmlContent={component.content} />;
        case "PartialOrderedList":
          return (
            <PartialXmlOrderedList key={index} xmlContent={component.content} />
          );
        case "code":
          return (
            <XmlCode
              key={index}
              xmlContent={component.content}
              pushSnack={pushSnack}
            />
          );
        default:
          return (
            <Typography
              key={index}
              variant="body"
              sx={{
                display: "inline",
                whiteSpace: "pre-line",
                wordBreak: "break-word",
              }}
            >
              {component.content}
            </Typography>
          );
      }
    }

    return component.content ? (
      <StreamingTextBox
        key={index}
        text={component.content}
        showCursor={showCursor}
      />
    ) : null;
  };

  // Find the last model message
  const modelMessages = messages.filter(
    (m) => m.source === "system" || m.source === "agent",
  );
  const lastModelMessage = modelMessages[modelMessages.length - 1];

  // Group consecutive model messages together, keeping track of which indices start a group
  const modelGroupStarts = new Map<number, ChatMessage[]>();
  let currentGroup: ChatMessage[] = [];
  let groupStartIndex = -1;

  for (let i = 0; i < messages.length; i++) {
    const message = messages[i];
    const isModel = message.source === "system" || message.source === "agent";

    if (isModel) {
      if (currentGroup.length === 0) {
        groupStartIndex = i;
      }
      currentGroup.push(message);
    } else {
      // User message - save current group if any
      if (currentGroup.length > 0) {
        modelGroupStarts.set(groupStartIndex, [...currentGroup]);
        currentGroup = [];
        groupStartIndex = -1;
      }
    }
  }
  // Don't forget the last group if it exists
  if (currentGroup.length > 0) {
    modelGroupStarts.set(groupStartIndex, [...currentGroup]);
  }

  return (
    <Box
      sx={{
        "*:is(h1, h2, h3, h4, h5, h6)": {
          marginTop: "16px",
          marginBottom: "8px",
        },
      }}
    >
      {messages.map((message, index) => {
        // User messages - always show
        if (message.source === "user") {
          return (
            <Box key={index} sx={{ marginBottom: "8px" }}>
              <UserMessageSection message={message} />
            </Box>
          );
        }

        // Model message - check if this is the start of a group
        const group = modelGroupStarts.get(index);
        if (!group) {
          // Not the start of a group, skip (will be rendered as part of its group)
          return null;
        }

        // Check if this group contains the last model message
        const groupContainsLast = group.includes(lastModelMessage);

        if (groupContainsLast) {
          // This group contains the last model message
          // Collapse all but the last, show the last expanded
          const toCollapse = group.slice(0, -1);
          const lastInGroup = group[group.length - 1];
          const lastInGroupIndex = messages.indexOf(lastInGroup);

          return (
            <Box key={`group-${index}`}>
              {toCollapse.length > 0 && (
                <CollapsedModelMessages
                  messages={toCollapse}
                  functionCalls={functionCalls}
                  parseMessages={parseMessages}
                  renderComponent={renderComponent}
                />
              )}
              <ModelMessageDisplay
                message={lastInGroup}
                functionCalls={functionCalls}
                messageIndex={lastInGroupIndex}
                isLastMessage={lastInGroupIndex === messages.length - 1}
                isCurrentlyActive={isCurrentlyActive}
                parseMessages={parseMessages}
                renderComponent={renderComponent}
                onCreateDocument={onCreateDocument}
                pushSnack={pushSnack}
              />
            </Box>
          );
        }

        // Group doesn't contain the last model message - collapse entire group
        return (
          <CollapsedModelMessages
            key={`collapsed-${index}`}
            messages={group}
            functionCalls={functionCalls}
            parseMessages={parseMessages}
            renderComponent={renderComponent}
          />
        );
      })}
    </Box>
  );
};

const CollapsedModelMessages = ({
  messages,
  functionCalls,
  parseMessages,
  renderComponent,
}: {
  messages: ChatMessage[];
  functionCalls: FunctionCall[];
  parseMessages: (text: string) => Array<{
    type: "text" | "xml";
    content: string;
    componentType?: string;
  }>;
  renderComponent: (
    component: {
      type: "text" | "xml";
      content: string;
      componentType?: string;
    },
    index: number | string,
    showCursor?: boolean,
  ) => React.ReactNode;
}) => {
  const [expanded, setExpanded] = useState(false);

  const getPreviewText = (content: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    const textContent = doc.body.textContent || "";
    const firstLine =
      textContent.split("\n").find((line) => line.trim().length > 0) || "";
    return firstLine.substring(0, 80) + (firstLine.length > 80 ? "..." : "");
  };

  const firstMessage = messages[0];
  const firstPreview =
    firstMessage?.source === "user"
      ? `You: ${getPreviewText(firstMessage.content)}`
      : getPreviewText(firstMessage?.content || "");
  const messageCount = messages.length;

  return (
    <Box sx={{ marginBottom: "8px" }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: "8px",
          cursor: "pointer",
          padding: "8px",
          backgroundColor: "rgba(0, 0, 0, 0.02)",
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <Box sx={{ marginTop: "2px", flexShrink: 0 }}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </Box>
        <Typography variant="bodySmall" color="text.secondary" sx={{ flex: 1 }}>
          {firstPreview}
          {messageCount > 1 && (
            <Typography
              component="span"
              variant="bodySmall"
              sx={{ marginLeft: "8px", opacity: 0.7 }}
            >
              (+{messageCount - 1} more)
            </Typography>
          )}
        </Typography>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ paddingLeft: "24px", marginTop: "8px" }}>
          {messages.map((message, msgIdx) => {
            if (message.source === "user") {
              return (
                <Box
                  key={msgIdx}
                  sx={{
                    marginBottom: msgIdx < messages.length - 1 ? "12px" : 0,
                  }}
                >
                  <UserMessageSection message={message} />
                </Box>
              );
            }
            const parsedComponents = parseMessages(message.content);
            return (
              <Box
                key={msgIdx}
                sx={{ marginBottom: msgIdx < messages.length - 1 ? "12px" : 0 }}
              >
                {parsedComponents.map((component, index) =>
                  renderComponent(
                    component,
                    `collapsed-${msgIdx}-${index}`,
                    false,
                  ),
                )}
                <ToolOutputsSection
                  functionCalls={functionCalls.filter(
                    (call) => message.outputCallIds?.includes(call.id),
                  )}
                />
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

const ModelMessageDisplay = ({
  message,
  functionCalls,
  messageIndex,
  isLastMessage,
  isCurrentlyActive,
  parseMessages,
  renderComponent,
  onCreateDocument,
  pushSnack,
}: {
  message: ChatMessage;
  functionCalls: FunctionCall[];
  messageIndex: number;
  isLastMessage: boolean;
  isCurrentlyActive: boolean;
  parseMessages: (
    text: string,
  ) => Array<{ type: "text" | "xml"; content: string; componentType?: string }>;
  renderComponent: (
    component: {
      type: "text" | "xml";
      content: string;
      componentType?: string;
    },
    index: number | string,
    showCursor?: boolean,
  ) => React.ReactNode;
  onCreateDocument?: (content: string) => void;
  pushSnack: any;
}) => {
  const parsedComponents = parseMessages(message.content);

  return (
    <Box sx={{ marginBottom: message.source === "user" ? "8px" : "0" }}>
      {message.source === "user" && <UserMessageSection message={message} />}
      {(message.source === "system" || message.source === "agent") && (
        <Box
          sx={{
            position: "relative",
            paddingTop: "20px",
            marginTop: "-10px",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              display: "flex",
              gap: "4px",
              zIndex: 1,
            }}
          >
            <Tooltip title="Copy to clipboard">
              <IconButton
                size="small"
                onClick={() => {
                  const parser = new DOMParser();
                  const doc = parser.parseFromString(
                    message.content,
                    "text/html",
                  );
                  const textContent = doc.body.textContent || "";

                  navigator.clipboard.write([
                    new ClipboardItem({
                      "text/html": new Blob([message.content], {
                        type: "text/html",
                      }),
                      "text/plain": new Blob([textContent], {
                        type: "text/plain",
                      }),
                    }),
                  ]);
                  pushSnack({
                    message: "Content copied to clipboard",
                    alertSeverity: "success",
                    key: "content-copied",
                  });
                }}
                sx={{
                  padding: "4px",
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                  },
                }}
              >
                <Copy size={16} />
              </IconButton>
            </Tooltip>
            {onCreateDocument && (
              <Tooltip title="Create task document">
                <IconButton
                  size="small"
                  onClick={() => {
                    onCreateDocument(message.content);
                  }}
                  sx={{
                    padding: "4px",
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                    },
                  }}
                >
                  <FilePlus2 size={16} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {parsedComponents.length === 1 &&
          parsedComponents[0].type === "text" ? (
            <StreamingTextBox
              text={message.content}
              showCursor={isCurrentlyActive && isLastMessage}
            />
          ) : (
            parsedComponents.map((component, index) =>
              renderComponent(
                component,
                `${messageIndex}-${index}`,
                isCurrentlyActive &&
                  isLastMessage &&
                  index === parsedComponents.length - 1,
              ),
            )
          )}
          <ToolOutputsSection
            functionCalls={functionCalls.filter(
              (call) => message.outputCallIds?.includes(call.id),
            )}
          />
        </Box>
      )}
    </Box>
  );
};

export const ThinkingSection = ({
  thinkingMessage,
  isActive,
}: {
  thinkingMessage: string;
  isActive: boolean;
}) => {
  if (!thinkingMessage || !isActive) return null;

  return (
    <Box>
      <Typography
        variant="bodySmall"
        color="text.secondary"
        sx={{ fontStyle: "italic" }}
      >
        {thinkingMessage}
      </Typography>
    </Box>
  );
};

const UserMessageSection = ({ message }: { message: ChatMessage }) => {
  return (
    <Box
      sx={{
        backgroundColor: "#f5f5f5",
        padding: "12px 16px",
        borderRadius: "8px",
        marginBottom: "8px",
      }}
    >
      <Typography
        variant="bodySmall"
        color="text.secondary"
        sx={{ marginBottom: "4px", fontWeight: 600 }}
      >
        You
      </Typography>
      <DocumentViewer
        minHeight="unset"
        content={message.contentHtml || message.content}
      />
    </Box>
  );
};

export const FollowUpInputSection = ({
  onFollowUpQuestion,
}: {
  onFollowUpQuestion?: (content: RichContent) => Promise<void>;
}) => {
  const editorRef = useRef<Handle>(null);
  const [followUpQuestionKey, setFollowUpQuestionKey] = useState("");
  const [content, setContent] = useCached<RichContent | null>(
    `follow-up-question`,
    null,
  );
  const _initialContent: string | JSONContent | undefined =
    (content?.text?.length && content?.json && JSON.parse(content?.json)) ||
    undefined;
  const [uploadsInProgress, setUploadsInProgress] = useState(false);

  const handleSubmit = async () => {
    if (!content?.text?.trim() && onFollowUpQuestion) {
      return;
    }
    if (content?.text?.trim() && onFollowUpQuestion) {
      await onFollowUpQuestion(content);
      setContent(null);
      setFollowUpQuestionKey(`follow-up-question-${Date.now()}`);
    }
  };

  return (
    <FlexBox direction="column" gap="8px">
      <Typography variant="h5">What else can I help with?</Typography>
      <SimpleRichEditor
        ref={editorRef}
        key={followUpQuestionKey}
        setContent={setContent}
        placeholder="Follow-up question"
        setUploadsInProgress={setUploadsInProgress}
        initialContent={_initialContent}
        onKeyDown={(_, e) => {
          if (e.key === "Enter" && !(e.shiftKey || e.ctrlKey || e.metaKey)) {
            if (!content?.text?.trim() || uploadsInProgress) return;
            e.preventDefault();
            handleSubmit();
          }
        }}
        primaryAction={
          <Button
            size="small"
            sx={{
              height: "unset",
              padding: "8px 18px",
            }}
            onClick={handleSubmit}
            disabled={!content?.text?.trim() || uploadsInProgress}
          >
            Send
          </Button>
        }
      />
    </FlexBox>
  );
};

export const AlertsSection = ({
  alerts,
  onDismissAlert,
}: {
  alerts: AlertItem[];
  onDismissAlert?: (alertId: string) => void;
}) => {
  if (!alerts.length) return null;

  return (
    <Box
      sx={{
        gap: "8px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <WithDividers>
        {alerts.map((alert: AlertItem) => (
          <AgentAlert
            key={alert.id}
            severity={alert.level}
            title={alert.title}
            description={alert.description}
            dismissable={alert.dismissable}
            onDismiss={
              alert.dismissable && onDismissAlert
                ? () => onDismissAlert(alert.id)
                : undefined
            }
          />
        ))}
      </WithDividers>
    </Box>
  );
};
